// doesn't fit project conventions because pasted from MUI site

import * as React from 'react';
import { Box, Autocomplete, Grid, Typography, TextField, useTheme } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import { inputLabelSx, textFieldSx } from '../lib/uiformatting';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };
const placesService = { current: null };

export default function MapsAutofill(props) {
  const [value, setValue] = React.useState(props.defaultValue);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const loaded = React.useRef(false);
  const theme = useTheme();

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    [],
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }
    if (!placesService.current && window.google) {
      placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }
    if (!placesService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    // where the fetch place options are
    fetch({ input: inputValue, types: ['address'] }, (results) => {
      if (active) {
        let newOptions = [

        ];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }
        // newOptions.forEach((op) => {
        // });
        newOptions = newOptions.filter((op) => op.terms && props.states.includes(op.terms[3].value));

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const [reason, setReason] = React.useState("");
  const [zip, setZip] = React.useState("");

  React.useEffect(() => {
    if (reason === "selectOption") {
      setReason("afterSelect");

      placesService.current.getDetails({ placeId: value.place_id, fields: ["address_components"] }, (place, status) => {
        setZip(place.address_components.find((comp) => comp.types.includes("postal_code")).long_name);
      });

      setValue({ ...value, description: value.structured_formatting.main_text });
    }
  }, [value]);

  React.useEffect(() => {
    if (reason === "afterSelect") {
      setReason("");
      props.onSelect({ ...value, zip: zip });
    }
  }, [zip]);

  return (
    <Autocomplete
      id="google-map-demo"
      sx={{ width: "100%" }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No locations"
      isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onClose={(e, reason) => {
        setReason(reason);
      }}
      renderInput={(params) =>
        <TextField
          sx={{
            ...textFieldSx,
            "& .MuiInputBase-input": {
              pl: 1,
            },
            "& .MuiInputLabel-root": {
              backgroundColor: theme.palette.background.paper,
              padding: "0 32px",
              transition: "all 0.2s ease-in-out",
            },
          }}
          {...params}
          label={props.label}
          value={props.value}
          onChange={props.onChange}
          error={props.error}
          helperText={props.helperText}
          required
          fullWidth
          InputLabelProps={{
            sx: inputLabelSx,
            "& .MuiInputLabel-shrink": {
              backgroundColor: theme.palette.background.paper,
            },
          }}
        />
      }
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
